/** Shared attachment model for every Outlook composer (Dean, 12 Oct 2026).
 *  item = { id, kind: "upload", file }                                                   — a browser File (chunk-uploaded on send)
 *       | { id, kind: "pick", source, key, name, size, mime_type, origin, scope }        — a document pulled server-side on send
 *  source: "case_file" (scope {case_id}) · "app_file" (scope {sf, item_id}) · "message_attachment" (scope {message_id}) */
import { uploadInboxAttachment } from "@/lib/api";

export const MAX_TOTAL = 25 * 1024 * 1024;
export const fmtSize = (n) => (n == null ? "size ?" : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
export const itemSize = (it) => (it.kind === "upload" ? it.file.size : it.size || 0);
export const totalBytes = (items) => items.reduce((s, it) => s + itemSize(it), 0);
export const uploadItem = (file) => ({ id: `up-${file.name}-${file.size}`, kind: "upload", file });
export const pickItem = (row, source, scope) => ({ id: `${source}:${row.key}`, kind: "pick", source, key: row.key, name: row.name, size: row.size ?? null, mime_type: row.mime_type || "", origin: row.origin, scope });

/** Merge new items in (dedupe by id) — returns null when the 25 MB cap would be broken. */
export function mergeItems(current, incoming) {
  const next = [...current];
  incoming.forEach((it) => { if (!next.some((x) => x.id === it.id)) next.push(it); });
  return totalBytes(next) > MAX_TOTAL ? null : next;
}

/** Pasted screenshots sit in the editor HTML as data: URIs. Pull them out as files (so they go through the chunked upload,
 *  not the JSON body) and point the <img> at a cid: the backend attaches inline (John Garcia, 28 Oct 2026). */
export function splitInlineImages(html) {
  if (!html || !/<img\b/i.test(html)) return { html: html || "", inline: [] };
  const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, "text/html");
  const inline = [];
  const stamp = Date.now().toString(36);
  doc.querySelectorAll("img").forEach((img, i) => {
    const src = img.getAttribute("src") || "";
    const m = /^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i.exec(src);
    if (!m) { if (!/^cid:/i.test(src)) img.remove(); return; }
    const bytes = Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
    const ext = (m[1].split("/")[1] || "png").replace("jpeg", "jpg").replace(/\+.*$/, "");
    const name = `screenshot-${i + 1}.${ext}`;
    const content_id = `img${i + 1}-${stamp}@simplyfunded.portal`;
    inline.push({ file: new File([bytes], name, { type: m[1] }), content_id });
    img.setAttribute("src", `cid:${content_id}`);
    if (!img.getAttribute("alt")) img.setAttribute("alt", name);
    img.setAttribute("style", "max-width:100%;height:auto");
  });
  return { html: doc.getElementById("r").innerHTML, inline };
}

export const hasInlineImage = (html) => /<img\b/i.test(html || "");

/** Upload the browser files (chunked, with progress) and turn every item into the backend's MailAttachment shape.
 *  `inline` = splitInlineImages(...).inline — uploaded the same way, flagged is_inline + content_id. */
export async function buildAttachmentsPayload(items, uploadChunk, setProgress, inline = []) {
  const out = [];
  const uploads = items.filter((it) => it.kind === "upload");
  const all = [...uploads.map((it) => ({ file: it.file })), ...inline];
  for (let i = 0; i < all.length; i++) {
    setProgress?.({ name: all[i].file.name, i: i + 1, n: all.length, pct: 0 });
    const up = await uploadInboxAttachment(uploadChunk, all[i].file, (pct) => setProgress?.((p) => ({ ...p, pct })));
    out.push({ source: "upload", ...up, ...(all[i].content_id ? { is_inline: true, content_id: all[i].content_id } : {}) });
  }
  setProgress?.(null);
  items.filter((it) => it.kind === "pick").forEach((it) => out.push({
    source: it.source, key: it.key, name: it.name, content_type: it.mime_type || "",
    case_id: it.scope?.case_id || "", sf: it.scope?.sf || "", item_id: it.scope?.item_id ? String(it.scope.item_id) : "", message_id: it.scope?.message_id || "",
  }));
  return out;
}
